class TravelSizeGiftPromotion extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.refreshController = null;
    this.refreshTimeout = null;
    this.observer = null;
  }

  connectedCallback() {
    this.observeCart();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.refreshController?.abort();

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
  }

  observeCart() {
    const cartRoot =
      document.querySelector('cart-items') ||
      document.querySelector('cart-drawer-items') ||
      document.querySelector('#main-cart-items') ||
      document.querySelector('#CartDrawer');

    if (!cartRoot) {
      return;
    }

    this.observer = new MutationObserver(() => {
      this.scheduleRefresh();
    });

    this.observer.observe(cartRoot, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['value']
    });
  }

  scheduleRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    this.refreshTimeout = setTimeout(() => {
      this.refresh();
    }, 250);
  }

  async refresh() {
    if (!this.sectionId) {
      return;
    }

    this.refreshController?.abort();
    this.refreshController = new AbortController();

    const url = new URL(
      window.location.pathname,
      window.location.origin
    );

    url.searchParams.set(
      'section_id',
      this.sectionId
    );

    try {
      const response = await fetch(url.toString(), {
        signal: this.refreshController.signal
      });

      if (!response.ok) {
        throw new Error(
          `Section request failed: ${response.status}`
        );
      }

      const html = await response.text();

      const documentFragment = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const updatedPromotion =
        documentFragment.querySelector(
          'travel-size-gift-promotion'
        );

      if (!updatedPromotion) {
        return;
      }

      const currentHasSelector = Boolean(
        this.querySelector(
          'travel-size-gift-selector'
        )
      );

      const updatedHasSelector = Boolean(
        updatedPromotion.querySelector(
          'travel-size-gift-selector'
        )
      );

      if (
        currentHasSelector &&
        updatedHasSelector
      ) {
        return;
      }

      if (!updatedHasSelector) {
        delete this.dataset.autoOpened;
      }

      this.innerHTML =
        updatedPromotion.innerHTML;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error(
        'Travel Size Gift refresh failed',
        error
      );
    }
  }
}

class TravelSizeGiftSelector extends HTMLElement {
  constructor() {
    super();

    this.dialog = this.querySelector(
      '[data-gift-selector-dialog]'
    );

    this.closeButton = this.querySelector(
      '[data-gift-selector-close]'
    );

    this.searchInput = this.querySelector(
      '[data-gift-selector-search]'
    );

    this.products = Array.from(
      this.querySelectorAll(
        '[data-gift-product]'
      )
    );

    this.addButtons = Array.from(
      this.querySelectorAll(
        '[data-gift-selector-add]'
      )
    );

    this.viewport = this.querySelector(
      '[data-gift-selector-viewport]'
    );

    this.track = this.querySelector(
      '[data-gift-selector-track]'
    );

    this.previousButton = this.querySelector(
      '[data-gift-selector-previous]'
    );

    this.nextButton = this.querySelector(
      '[data-gift-selector-next]'
    );

    this.currentPageElement = this.querySelector(
      '[data-gift-selector-current-page]'
    );

    this.totalPagesElement = this.querySelector(
      '[data-gift-selector-total-pages]'
    );

    this.currentPage = 0;

    this.handleResize =
      this.handleResize.bind(this);

    this.bindEvents();
  }

  connectedCallback() {
    window.addEventListener(
      'resize',
      this.handleResize
    );

    queueMicrotask(() => {
      this.currentPage = 0;
      this.updateCarousel();
      this.openAutomatically();
    });
  }

  disconnectedCallback() {
    window.removeEventListener(
      'resize',
      this.handleResize
    );
  }

  bindEvents() {
    this.previousButton?.addEventListener(
      'click',
      () => {
        this.previousPage();
      }
    );

    this.nextButton?.addEventListener(
      'click',
      () => {
        this.nextPage();
      }
    );

    this.closeButton?.addEventListener(
      'click',
      () => this.close()
    );

    this.dialog?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) {
          this.close();
        }
      }
    );

    this.searchInput?.addEventListener(
      'input',
      (event) => {
        this.filterProducts(
          event.target.value
        );
      }
    );

    this.addButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          this.addGift(button);
        }
      );
    });
  }

  handleResize() {
    const totalPages =
      this.getTotalPages();

    if (
      this.currentPage >
      totalPages - 1
    ) {
      this.currentPage =
        Math.max(
          totalPages - 1,
          0
        );
    }

    this.updateCarousel();
  }

  openAutomatically() {
    const promotion = this.closest(
      'travel-size-gift-promotion'
    );

    if (
      !promotion ||
      !this.dialog
    ) {
      return;
    }

    if (
      promotion.dataset.autoOpened ===
      'true'
    ) {
      return;
    }

    if (this.dialog.open) {
      return;
    }

    promotion.dataset.autoOpened =
      'true';

    this.dialog.showModal();

    requestAnimationFrame(() => {
      this.updateCarousel();
    });
  }

  open() {
    if (
      !this.dialog ||
      this.dialog.open
    ) {
      return;
    }

    this.dialog.showModal();

    requestAnimationFrame(() => {
      this.updateCarousel();
    });
  }

  close() {
    if (
      !this.dialog ||
      !this.dialog.open
    ) {
      return;
    }

    this.dialog.close();
  }

  getVisibleItems() {
    return window.matchMedia(
      '(max-width: 749px)'
    ).matches
      ? 1
      : 3;
  }

  getVisibleProducts() {
    return this.products.filter(
      (product) =>
        !product.hidden
    );
  }

  getTotalPages() {
    const products =
      this.getVisibleProducts();

    const visibleItems =
      this.getVisibleItems();

    return Math.max(
      1,
      Math.ceil(
        products.length /
          visibleItems
      )
    );
  }

  nextPage() {
    const totalPages =
      this.getTotalPages();

    if (
      totalPages <= 1
    ) {
      return;
    }

    this.currentPage =
      this.currentPage >=
      totalPages - 1
        ? 0
        : this.currentPage + 1;

    this.updateCarousel();
  }

  previousPage() {
    const totalPages =
      this.getTotalPages();

    if (
      totalPages <= 1
    ) {
      return;
    }

    this.currentPage =
      this.currentPage <= 0
        ? totalPages - 1
        : this.currentPage - 1;

    this.updateCarousel();
  }

  updateCarousel() {
    if (!this.track) {
      return;
    }

    const visibleProducts =
      this.getVisibleProducts();

    const visibleItems =
      this.getVisibleItems();

    const totalPages =
      this.getTotalPages();

    if (
      this.currentPage >
      totalPages - 1
    ) {
      this.currentPage = 0;
    }

    if (
      visibleProducts.length === 0
    ) {
      this.track.style.transform =
        'translateX(0)';

      this.updatePagination(
        0,
        0
      );

      this.updateArrows(0);

      return;
    }

    const firstProduct =
      visibleProducts[0];

    const cardWidth =
      firstProduct.getBoundingClientRect()
        .width;

    const trackStyles =
      window.getComputedStyle(
        this.track
      );

    const gap = parseFloat(
      trackStyles.columnGap ||
        trackStyles.gap ||
        0
    );

    const pageWidth =
      visibleItems *
        cardWidth +
      Math.max(
        visibleItems - 1,
        0
      ) *
        gap;

    const offset =
      this.currentPage *
      pageWidth;

    this.track.style.transform =
      `translateX(-${offset}px)`;

    this.updatePagination(
      this.currentPage + 1,
      totalPages
    );

    this.updateArrows(
      totalPages
    );
  }

  updatePagination(
    currentPage,
    totalPages
  ) {
    if (
      this.currentPageElement
    ) {
      this.currentPageElement.textContent =
        String(currentPage);
    }

    if (
      this.totalPagesElement
    ) {
      this.totalPagesElement.textContent =
        String(totalPages);
    }
  }

  updateArrows(totalPages) {
    const shouldDisable =
      totalPages <= 1;

    if (
      this.previousButton
    ) {
      this.previousButton.disabled =
        shouldDisable;
    }

    if (
      this.nextButton
    ) {
      this.nextButton.disabled =
        shouldDisable;
    }
  }

  filterProducts(searchTerm) {
    const normalizedSearchTerm =
      searchTerm
        .trim()
        .toLocaleLowerCase(
          'pt-BR'
        );

    this.products.forEach(
      (product) => {
        const productTitle =
          product.dataset.productTitle
            ?.toLocaleLowerCase(
              'pt-BR'
            );

        product.hidden =
          !productTitle?.includes(
            normalizedSearchTerm
          );
      }
    );

    this.currentPage = 0;

    requestAnimationFrame(() => {
      this.updateCarousel();
    });
  }

  getSections() {
    const sections = [];

    if (
      document.querySelector(
        '#main-cart-items'
      )
    ) {
      sections.push(
        'main-cart-items'
      );
    }

    if (
      document.querySelector(
        '#main-cart-footer'
      )
    ) {
      sections.push(
        'main-cart-footer'
      );
    }

    if (
      document.querySelector(
        'cart-drawer'
      )
    ) {
      sections.push(
        'cart-drawer'
      );
    }

    if (
      document.querySelector(
        '#cart-icon-bubble'
      )
    ) {
      sections.push(
        'cart-icon-bubble'
      );
    }

    return sections;
  }

  async addGift(button) {
    const variantId = Number(
      button.dataset.variantId
    );

    if (!variantId) {
      return;
    }

    this.setLoading(
      button,
      true
    );

    try {
      const sections =
        this.getSections();

      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1
              }
            ],
            sections,
            sections_url:
              window.location.pathname
          })
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.description ||
            data.message ||
            'Não foi possível adicionar o Travel Size.'
        );
      }

      this.close();

      if (data.sections) {
        this.renderSections(
          data.sections
        );
      }

      await this.refreshPromotion();
    } catch (error) {
      console.error(
        'Travel Size Gift add failed',
        error
      );

      this.showError(
        error.message
      );
    } finally {
      this.setLoading(
        button,
        false
      );
    }
  }

  renderSections(sections) {
    this.replaceSection(
      sections['main-cart-items'],
      '#main-cart-items',
      '#main-cart-items'
    );

    this.replaceSection(
      sections['main-cart-footer'],
      '#main-cart-footer',
      '#main-cart-footer'
    );

    this.replaceSection(
      sections['cart-icon-bubble'],
      '#cart-icon-bubble',
      '#cart-icon-bubble'
    );

    this.replaceSection(
      sections['cart-drawer'],
      'cart-drawer',
      'cart-drawer'
    );
  }

  replaceSection(
    html,
    currentSelector,
    newSelector
  ) {
    if (!html) {
      return;
    }

    const currentElement =
      document.querySelector(
        currentSelector
      );

    if (!currentElement) {
      return;
    }

    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const updatedElement =
      parsedDocument.querySelector(
        newSelector
      );

    if (!updatedElement) {
      return;
    }

    currentElement.replaceWith(
      updatedElement
    );
  }

  async refreshPromotion() {
    const promotion =
      document.querySelector(
        'travel-size-gift-promotion'
      );

    if (
      !promotion ||
      typeof promotion.refresh !==
        'function'
    ) {
      return;
    }

    await promotion.refresh();
  }

  setLoading(
    button,
    loading
  ) {
    if (loading) {
      button.dataset.originalText =
        button.textContent;

      button.textContent =
        'Adicionando...';

      button.disabled = true;

      return;
    }

    button.textContent =
      button.dataset.originalText ||
      'Escolher';

    button.disabled = false;
  }

  showError(message) {
    let errorElement =
      this.querySelector(
        '[data-gift-selector-error]'
      );

    if (!errorElement) {
      errorElement =
        document.createElement('p');

      errorElement.dataset.giftSelectorError =
        '';

      errorElement.className =
        'travel-size-gift-selector__error';

      this.dialog
        ?.querySelector(
          '.travel-size-gift-selector__header'
        )
        ?.after(errorElement);
    }

    errorElement.textContent =
      message;
  }
}

if (
  !customElements.get(
    'travel-size-gift-promotion'
  )
) {
  customElements.define(
    'travel-size-gift-promotion',
    TravelSizeGiftPromotion
  );
}

if (
  !customElements.get(
    'travel-size-gift-selector'
  )
) {
  customElements.define(
    'travel-size-gift-selector',
    TravelSizeGiftSelector
  );
}